import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    forwardRef,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    takeUntil,
} from 'rxjs/operators';
import { hasValidator } from '../_helpers/functions';
import { AutocompleteService } from './autocomplete.service';

/**
 * Componente de autocomplete para filtro local de listas.
 *
 * Modo string (padrão): aceita string[] e armazena o texto selecionado no FormControl.
 *
 * @example
 * <sume-input-autocomplete
 *   label="Projeto"
 *   [formGroup]="formGroup"
 *   controlName="projeto"
 *   [items]="projetos">
 * </sume-input-autocomplete>
 *
 * Modo objeto: aceita object[] com itemKeyName e itemValueName.
 * O FormControl armazenará o valor de itemKeyName; o display mostrará itemValueName.
 *
 * @example
 * <sume-input-autocomplete
 *   label="Colaborador"
 *   [formGroup]="formGroup"
 *   controlName="id_colaborador"
 *   [items]="colaboradores"
 *   itemKeyName="id_colaborador"
 *   itemValueName="nome">
 * </sume-input-autocomplete>
 */

@Component({
    selector: 'sume-input-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true,
        },
    ],
})
export class AutocompleteComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    @Input() label: string;
    @Input() formGroup: FormGroup;
    @Input() controlName: string;
    @Input() itemKeyName?: string;
    @Input() itemValueName?: string;
    @Input() changeText = false;
    @Input() clearOnNotFound = false;
    @Input() showAll = false;
    @Input() helpMessage?: string;
    @Input() readOnly = false;
    @Output() current = new EventEmitter<any>();
    @Output() onSelect = new EventEmitter<any>();
    @Output() onClear = new EventEmitter<void>();

    @ViewChild('instance', { static: true }) instance: NgbTypeahead;

    private readonly _destroyed$ = new Subject<void>();
    private readonly _items$ = new BehaviorSubject<any[]>([]);
    private _displayValues: string[] = [];

    focus$ = new Subject<string>();
    click$ = new Subject<string>();
    searchControl = new FormControl('');
    isRequired = false;

    private _onChange: any = () => {};
    private _onTouched: any = () => {};

    constructor(private _autocompleteService: AutocompleteService) {}

    get isStringMode(): boolean {
        return !this.itemKeyName || !this.itemValueName;
    }

    @Input()
    get items(): any[] {
        return this._items$.value;
    }
    set items(value: any[] | undefined) {
        this._items$.next(value || []);
        this._updateDisplayValues();
        if (value?.length && this.formControl?.value != null) {
            this._syncFormValue();
        }
    }

    get formControl(): AbstractControl {
        return this.formGroup?.get(this.controlName);
    }

    ngOnInit(): void {
        const ctrl = this.formGroup?.controls[this.controlName];
        this.isRequired = !!ctrl?.validator && hasValidator(ctrl, 'required');
        this._setupSubscriptions();
        if (this.formControl?.value != null) {
            this._syncFormValue();
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
        this.focus$.complete();
        this.click$.complete();
        this._items$.complete();
    }

    // --- ControlValueAccessor ---

    writeValue(_: any): void {
        this._syncFormValue();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.searchControl.disable() : this.searchControl.enable();
    }

    // --- NgbTypeahead ---

    search = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(
            debounceTime(200),
            distinctUntilChanged()
        );
        const clicksWithClosedPopup$ = this.click$.pipe(
            filter(() => !this.instance?.isPopupOpen())
        );
        return merge(debouncedText$, this.focus$, clicksWithClosedPopup$).pipe(
            map(term =>
                this._autocompleteService.filterItems(this._displayValues, term, this.showAll ? Infinity : undefined)
            )
        );
    };

    // --- Handlers de eventos do template ---

    onItemSelected(term: string): void {
        const resolved = this._resolveSelection(term);
        if (!resolved) {
            this._clearValues();
            this._onTouched();
            return;
        }
        this.formControl?.setValue(resolved.key, { emitEvent: true });
        this.searchControl.setValue(resolved.display, { emitEvent: false });
        this._onChange(resolved.key);
        this.current.emit(resolved.key);
        this.onSelect.emit(resolved.key);
        this._onTouched();
    }

    onBlurClear(): void {
        if (!this.searchControl.value?.trim()) {
            this._clearValues();
            return;
        }
        if (!this.isStringMode) {
            const exists = this.items?.some(
                i => i[this.itemValueName] === this.searchControl.value
            );
            if (!exists && this.clearOnNotFound) {
                this._clearValues();
            }
        }
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !this.searchControl.value?.trim()) {
            this._clearValues();
        }
    }

    // --- Helpers privados ---

    private _resolveSelection(
        term: string
    ): { key: any; display: string } | null {
        if (this.isStringMode) return { key: term, display: term };
        const item = this.items?.find(i => i[this.itemValueName] === term);
        return item
            ? { key: item[this.itemKeyName], display: item[this.itemValueName] }
            : null;
    }

    private _resolveDisplayValue(value: any): string | null {
        if (this.isStringMode) return value;
        if (!this._items$.value?.length) return null;
        const item = this._items$.value.find(
            i => i[this.itemKeyName] === value
        );
        if (item) return item[this.itemValueName];
        return typeof value === 'string' ? value : null;
    }

    private _syncFormValue(): void {
        const value = this.formControl?.value;
        if (value == null) {
            this.searchControl.setValue('', { emitEvent: false });
            return;
        }
        const display = this._resolveDisplayValue(value);
        if (display != null && this.searchControl.value !== display) {
            this.searchControl.setValue(display, { emitEvent: false });
        }
    }

    private _updateDisplayValues(): void {
        this._displayValues = this.isStringMode
            ? (this._items$.value as string[])
            : this._items$.value.map(i => i[this.itemValueName]);
    }

    private _clearValues(): void {
        const hadValue =
            this.formControl?.value != null || !!this.searchControl.value;
        this.formControl?.setValue(null, { emitEvent: true });
        this.searchControl.setValue('', { emitEvent: false });
        this._onChange(null);
        if (hadValue) {
            this.current.emit(null);
            this.onClear.emit();
        }
    }

    private _setupSubscriptions(): void {
        merge(this.formControl?.statusChanges || [], this._items$)
            .pipe(
                filter(() => !!this.formControl),
                takeUntil(this._destroyed$)
            )
            .subscribe(() => {
                const shouldDisable =
                    this.formControl.disabled || !this.items?.length;
                shouldDisable
                    ? this.searchControl.disable()
                    : this.searchControl.enable();
            });

        this.formControl?.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this._destroyed$))
            .subscribe(() => this._syncFormValue());

        this.searchControl.valueChanges
            .pipe(
                filter(v => !v),
                distinctUntilChanged(),
                takeUntil(this._destroyed$)
            )
            .subscribe(() => {
                if (this.formControl?.value != null) {
                    this.formControl.setValue(null, { emitEvent: true });
                    this._onChange(null);
                    this.current.emit(null);
                    this.onClear.emit();
                }
                this._onTouched();
            });
    }
}
